import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { getLeads, getInsights } from '../api/client';
import client from '../api/client';
import { useAccount } from '../context/AccountContext';
import { Skeleton } from '../components/Layout';
import { C, Card, Btn, Tag, Toast, initials, useNotify } from '../utils/styles';

const STAGE_COLORS = { New:C.primary, Contacted:C.purple, Qualified:C.warning, Proposal:C.green, Won:C.success, Lost:C.danger };
const STAGES       = ['New','Contacted','Qualified','Proposal','Won','Lost'];
const PIE_COLORS   = ['#0099FF','#22c55e','#8b5cf6','#f59e0b','#ef4444'];

const PERIOD_OPTIONS = [
  { value:'daily',       label:'Daily (30 days)'     },
  { value:'weekly',      label:'Weekly (12 weeks)'   },
  { value:'monthly',     label:'Monthly (12 months)' },
  { value:'quarterly',   label:'Quarterly'            },
  { value:'semi_yearly', label:'Semi-yearly'          },
  { value:'yearly',      label:'Yearly (5 years)'     },
];

const pf  = (n) => `₹${Number(n||0).toLocaleString('en-IN')}`;
const fmt = (n) => Number(n||0).toLocaleString('en-IN');

// ── Shared stat card ─────────────────────────────────────────
const StatCard = ({ label, value, icon, color, sub }) => (
  <div style={{ background:'#fff', borderRadius:12, border:`1px solid ${C.border}`, padding:'16px 20px' }}>
    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
      <i className={`ti ${icon}`} style={{ fontSize:18, color:color||C.primary }} />
      <span style={{ fontSize:12, color:C.muted }}>{label}</span>
    </div>
    <div style={{ fontSize:26, fontWeight:700, color:C.dark, marginBottom:4 }}>{value}</div>
    {sub && <div style={{ fontSize:11, color:C.success }}>▲ {sub}</div>}
  </div>
);

const StatSkeleton = () => (
  <div style={{ background:'#fff', borderRadius:12, border:`1px solid ${C.border}`, padding:'16px 20px' }}>
    <Skeleton height={12} width="50%" style={{ marginBottom:12 }} />
    <Skeleton height={28} width="40%" style={{ marginBottom:8 }} />
    <Skeleton height={10} width="60%" />
  </div>
);

// ── Platform / Agency overview (shared layout) ────────────────
function AggregatedDashboard({ statsEndpoint, title, subtitle, accountLabel }) {
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(false);
  const [period,  setPeriod]  = useState('monthly');

  useEffect(() => {
    setLoading(true);
    setError(false);
    client.get(`${statsEndpoint}?period=${period}`)
      .then(({ data }) => setStats(data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [period, statsEndpoint]);

  const pieData = stats ? (
    accountLabel === 'Total Accounts'
      ? [
          { name:'Agency Accounts', value: stats.accounts?.agency || 0 },
          { name:'Sub-Accounts',    value: stats.accounts?.sub    || 0 },
        ].filter(d => d.value > 0)
      : [
          { name:'Active',    value: stats.accounts?.active    || 0 },
          { name:'Suspended', value: stats.accounts?.suspended || 0 },
        ].filter(d => d.value > 0)
  ) : [];

  const stageData = stats
    ? Object.entries(stats.pipeline?.by_stage || {}).map(([name, value]) => ({ name, value }))
    : [];

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:700, color:C.dark, marginBottom:2 }}>{title}</h1>
          <p style={{ fontSize:13, color:C.muted }}>{subtitle}</p>
        </div>
        <select value={period} onChange={e => setPeriod(e.target.value)}
          style={{ padding:'8px 12px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13, outline:'none', color:C.text, background:'#fff', cursor:'pointer' }}>
          {PERIOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {error && (
        <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:12, padding:16, marginBottom:20, fontSize:13, color:'#991b1b' }}>
          <i className="ti ti-alert-circle" style={{ marginRight:8 }} />
          Failed to load stats. Please check the backend is deployed with the latest accounts.js.
        </div>
      )}

      {/* Top stat cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:20 }}>
        {loading || !stats ? Array(8).fill(null).map((_,i) => <StatSkeleton key={i} />) : <>
          <StatCard label={accountLabel}      value={fmt(stats.accounts?.total)}      icon="ti-building"       color="#8b5cf6" sub={`${stats.accounts?.active||0} active`} />
          <StatCard label="Active Users"      value={fmt(stats.users?.total)}         icon="ti-users"          color="#f59e0b" />
          <StatCard label="Total Contacts"    value={fmt(stats.contacts?.total)}      icon="ti-address-book"   color="#0099FF" />
          <StatCard label="Pipeline Value"    value={pf(stats.pipeline?.total_value)} icon="ti-git-branch"     color="#22c55e" sub="all accounts" />
          <StatCard label="Won Revenue"       value={pf(stats.pipeline?.won_value)}   icon="ti-trophy"         color="#f59e0b" sub={`${fmt(stats.pipeline?.won_count)} deals`} />
          <StatCard label="Campaigns Sent"    value={fmt(stats.campaigns?.sent)}      icon="ti-mail-forward"   color="#8b5cf6" sub={`${fmt(stats.campaigns?.total)} total`} />
          <StatCard label="Suspended"         value={fmt(stats.accounts?.suspended)}  icon="ti-pause"          color="#ef4444" />
          <StatCard label="Conversion"        value={stats.pipeline?.won_count && stats.contacts?.total ? `${Math.round((stats.pipeline.won_count/stats.contacts.total)*100)}%` : '0%'} icon="ti-trending-up" color="#22c55e" sub="Won vs total leads" />
        </>}
      </div>

      {/* Charts */}
      {!loading && stats && (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:16, marginBottom:16 }}>
            {/* Revenue chart */}
            <div style={{ background:'#fff', borderRadius:12, border:`1px solid ${C.border}`, padding:20 }}>
              <div style={{ fontWeight:600, fontSize:14, color:C.dark, marginBottom:16 }}>
                Won Revenue — {PERIOD_OPTIONS.find(o => o.value===period)?.label}
              </div>
              {!stats.revenue_chart?.length ? (
                <div style={{ height:200, display:'flex', alignItems:'center', justifyContent:'center', color:C.faint, fontSize:13 }}>No won deals in this period</div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={stats.revenue_chart} margin={{ top:0, right:0, left:0, bottom:0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="label" tick={{ fontSize:11, fill:'#9ca3af' }} />
                    <YAxis tick={{ fontSize:11, fill:'#9ca3af' }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={v => [pf(v), 'Won Revenue']} />
                    <Bar dataKey="value" fill={C.primary} radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Pipeline by stage */}
            <div style={{ background:'#fff', borderRadius:12, border:`1px solid ${C.border}`, padding:20 }}>
              <div style={{ fontWeight:600, fontSize:14, color:C.dark, marginBottom:16 }}>Pipeline by Stage</div>
              {!stageData.length ? (
                <div style={{ height:200, display:'flex', alignItems:'center', justifyContent:'center', color:C.faint, fontSize:13 }}>No pipeline data</div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={stageData} layout="vertical" margin={{ top:0, right:20, left:60, bottom:0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize:11, fill:'#9ca3af' }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize:11, fill:'#374151' }} width={60} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#22c55e" radius={[0,4,4,0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Account breakdown pie + top accounts */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:16 }}>
            {/* Pie chart — account breakdown */}
            <div style={{ background:'#fff', borderRadius:12, border:`1px solid ${C.border}`, padding:20 }}>
              <div style={{ fontWeight:600, fontSize:14, color:C.dark, marginBottom:16 }}>Account Breakdown</div>
              {!pieData.length ? (
                <div style={{ height:160, display:'flex', alignItems:'center', justifyContent:'center', color:C.faint, fontSize:13 }}>No data</div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={3}>
                        {pieData.map((_,i) => <Cell key={i} fill={PIE_COLORS[i%PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ display:'flex', flexDirection:'column', gap:6, marginTop:8 }}>
                    {pieData.map((d,i) => (
                      <div key={d.name} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:12 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <div style={{ width:10, height:10, borderRadius:2, background:PIE_COLORS[i%PIE_COLORS.length], flexShrink:0 }} />
                          <span style={{ color:C.text }}>{d.name}</span>
                        </div>
                        <span style={{ fontWeight:600, color:C.dark }}>{d.value}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Top accounts by contacts */}
            <div style={{ background:'#fff', borderRadius:12, border:`1px solid ${C.border}`, padding:20 }}>
              <div style={{ fontWeight:600, fontSize:14, color:C.dark, marginBottom:16 }}>Top Accounts by Contacts</div>
              {!stats.top_accounts?.length ? (
                <div style={{ height:160, display:'flex', alignItems:'center', justifyContent:'center', color:C.faint, fontSize:13 }}>No data yet</div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {stats.top_accounts.map((acc, i) => (
                    <div key={acc.account_id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'#f9fafb', borderRadius:10 }}>
                      <div style={{ width:28, height:28, borderRadius:6, background:`${PIE_COLORS[i%PIE_COLORS.length]}22`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <span style={{ fontSize:11, fontWeight:700, color:PIE_COLORS[i%PIE_COLORS.length] }}>{i+1}</span>
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:12, fontWeight:500, color:C.dark, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{acc.name}</div>
                        <div style={{ fontSize:11, color:C.muted }}>{fmt(acc.contacts)} contacts</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Sub-account dashboard (original) ─────────────────────────
function SubAccountDashboard() {
  const [leads,     setLeads]     = useState([]);
  const [insights,  setInsights]  = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [notification, notify]    = useNotify();

  useEffect(() => {
    getLeads({ limit:100 })
      .then(({ data }) => setLeads(data.leads||[]))
      .catch(() => notify('Failed to load leads','error'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const fetchInsights = async () => {
    setAiLoading(true);
    try { const { data } = await getInsights(); setInsights(data); }
    catch { notify('AI insights failed','error'); }
    finally { setAiLoading(false); }
  };

  const totalValue = leads.reduce((a,b) => a+(b.value||0), 0);
  const wonLeads   = leads.filter(l => l.stage==='Won');
  const wonValue   = wonLeads.reduce((a,b) => a+(b.value||0), 0);
  const convRate   = leads.length ? Math.round((wonLeads.length/leads.length)*100) : 0;

  return (
    <div>
      <Toast {...notification} />

      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:700, color:C.dark, marginBottom:2 }}>Dashboard</h1>
          <p style={{ fontSize:13, color:C.muted }}>Welcome back — here's your pipeline overview</p>
        </div>
        <Btn onClick={fetchInsights} disabled={aiLoading}>
          <i className="ti ti-brain" style={{ fontSize:15 }} />
          {aiLoading ? 'Analysing...' : 'AI Insights'}
        </Btn>
      </div>

      {loading ? (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:20 }}>
          {Array(4).fill(null).map((_,i) => <StatSkeleton key={i} />)}
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:20 }}>
          <StatCard label="Total Leads"     value={leads.length}                          icon="ti-users"        sub="All time" />
          <StatCard label="Pipeline Value"  value={`₹${(totalValue/1000).toFixed(0)}K`}  icon="ti-chart-line"   sub="Active pipeline" />
          <StatCard label="Won Deals"       value={wonLeads.length}                       icon="ti-trophy"       color={C.success} sub={`₹${(wonValue/1000).toFixed(0)}K closed`} />
          <StatCard label="Conversion Rate" value={`${convRate}%`}                        icon="ti-trending-up"  sub="Won vs total" />
        </div>
      )}

      {insights && (
        <div style={{ background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:12, padding:'16px 20px', marginBottom:20 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
            <i className="ti ti-brain" style={{ fontSize:18, color:C.primary }} />
            <span style={{ fontWeight:600, fontSize:14, color:C.dark }}>AI Pipeline Insights</span>
          </div>
          <p style={{ fontSize:13, color:'#0c4a6e', lineHeight:1.7 }}>{insights.insights}</p>
        </div>
      )}

      {!loading && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
          <Card title="Pipeline by Stage" icon="ti-git-branch">
            {STAGES.map(stage => {
              const count = leads.filter(l => l.stage===stage).length;
              const pct   = leads.length ? Math.round((count/leads.length)*100) : 0;
              return (
                <div key={stage} style={{ marginBottom:12 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
                    <span style={{ fontSize:12, color:C.text }}>{stage}</span>
                    <span style={{ fontSize:12, fontWeight:600, color:STAGE_COLORS[stage] }}>{count}</span>
                  </div>
                  <div style={{ background:'#f3f4f6', borderRadius:4, height:6 }}>
                    <div style={{ width:`${pct}%`, height:6, borderRadius:4, background:STAGE_COLORS[stage], transition:'width 0.5s ease' }} />
                  </div>
                </div>
              );
            })}
          </Card>

          <Card title="Recent Leads" icon="ti-users">
            {leads.length === 0 ? (
              <div style={{ textAlign:'center', padding:20, color:C.faint, fontSize:13 }}>No leads yet.</div>
            ) : leads.slice(0,6).map(l => (
              <div key={l.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 0', borderBottom:`1px solid ${C.border}` }}>
                <div style={{ width:32, height:32, borderRadius:'50%', background:C.primary, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:11, fontWeight:700, flexShrink:0 }}>
                  {initials(l.name)}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.name}</div>
                  <div style={{ fontSize:11, color:C.muted }}>{l.company}</div>
                </div>
                <Tag label={l.stage} color={STAGE_COLORS[l.stage]} />
              </div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}

// ── Unified entry point ───────────────────────────────────────
export default function Dashboard() {
  const { isPlatform, isAgency, loading } = useAccount();

  if (loading) return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14 }}>
      {Array(8).fill(null).map((_,i) => <StatSkeleton key={i} />)}
    </div>
  );

  if (isPlatform) return (
    <AggregatedDashboard
      statsEndpoint="/api/accounts/stats"
      title="Platform Overview"
      subtitle="Aggregated metrics across all accounts"
      accountLabel="Total Accounts"
    />
  );

  if (isAgency) return (
    <AggregatedDashboard
      statsEndpoint="/api/accounts/agency-stats"
      title="Agency Dashboard"
      subtitle="Aggregated metrics across your sub-accounts"
      accountLabel="Sub-Accounts"
    />
  );

  return <SubAccountDashboard />;
}
